import { createAsyncThunk } from "@reduxjs/toolkit";
import { axiosApiInstance } from "@/helper/axios";
import { API_PORTAL_URL } from "@/lib/config";
import { ErrorToast, SuccessToast } from "@/components/toast/Toasts";
import { ISeoTag, ISeoTagDetails } from "./seotagModel";
import { cache } from "react";

export const getAllSeoTag = createAsyncThunk(
  "SeoTags/FindAllSeoTags",
  async () => {
    try {
      const response = await axiosApiInstance.post(
        `${API_PORTAL_URL}/SeoTags/FindAllSeoTags`,
        {
          PageName: "",
        },
      );
      return response.data?.Data.map((item: any) => ({
        ...item,
        IsActive: item.IsActive || false,
      }));
    } catch (error: any) {
      console.error("Error Fetching SeoTag:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const createSeoTag = createAsyncThunk(
  "SeoTags/InsertSeoTags",
  async (SeoTagData: ISeoTagDetails) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_PORTAL_URL}/SeoTags/InsertSeoTags`,
        SeoTagData,
      );
      SuccessToast(response.data?.Details || "Success");
      return response.data;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      console.error("Error creating SeoTag:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const updateSeoTag = createAsyncThunk(
  "SeoTags/UpdateSeoTags",
  async (SeoTagData: ISeoTagDetails) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_PORTAL_URL}/SeoTags/UpdateSeoTags`,
        SeoTagData,
      );
      SuccessToast(response.data?.Details || "Success");
      return response.data;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      console.error("Error updating SeoTag:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const getSeoTagByID = createAsyncThunk(
  "SeoTags/FindByIDSeoTags",
  async (ID: number) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_PORTAL_URL}/SeoTags/FindByIDSeoTags`,
        { ID },
      );
      const result = response.data?.Data as any;
      return {
        ID: result.ID,
        PageName: result.PageName,
        PageUrl: result.PageUrl,
        MetaTitle: result?.MetaTitle || "",
        MetaKeywords: result?.MetaKeywords || "",
        MetaDescription: result?.MetaDescription || "",
        IsActive: result.IsActive || false,
      } as ISeoTag;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      console.error("Error fetching SeoTag:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const seoTagActiveInactive = createAsyncThunk(
  "SeoTags/ActiveInActiveSeoTags",
  async (formData: any) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_PORTAL_URL}/SeoTags/ActiveInActiveSeoTags`,
        formData,
      );
      SuccessToast(response.data?.Details || "Success");
      return response.data;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      console.error("Error active-inactive SeoTag:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const deleteSeoTag = createAsyncThunk(
  "SeoTags/DeleteSeoTags",
  async (ID: number) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_PORTAL_URL}/SeoTags/DeleteSeoTags`,
        {
          ID,
        },
      );
      SuccessToast(response.data?.Details || "Success");
      return response.data;
    } catch (error: any) {
      ErrorToast(error?.response?.data?.Details || "Something went wrong");
      console.error("Error deleting SeoTag:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);
/**
 * Fetches SEO metadata for a given page URL.
 * Uses React.cache to memoize the request during a single server render pass.
 * This prevents multiple calls to SeoTags/FindAllSeoTags (e.g., in generateMetadata and the Page component).
 */
export const getSeoMetadata = cache(async (pageUrl: string) => {
  try {
    const res = await fetch(`${API_PORTAL_URL}/SeoTags/FindAllSeoTags`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ PageUrl: pageUrl }),
      next: { revalidate: 1 },
    });

    if (!res.ok) {
      console.warn(
        `[SEO] Could not fetch tags for ${pageUrl}: ${res.status} ${res.statusText}. Using fallback metadata.`,
      );
      return null;
    }

    const data = await res.json();
    return data?.Data?.[0] || null;
  } catch (error) {
    console.error(`Error fetching SEO metadata for ${pageUrl}:`, error);
    return null;
  }
});
